import type { RootCauseAnalysis } from '../types/issue';
import { ensureIshikawa, isLegacyHypothesisId } from './causeDraft';

/** Chaves de etapa do workspace. Evidência de problema rápido usa o subconjunto que ele já tem. */
export const EVIDENCE_STAGES = ['ident', 'analysis', 'root', 'plan', 'exec', 'valid', 'monitor', 'close'] as const;

export type EvidenceStage = (typeof EVIDENCE_STAGES)[number];

export type EvidenceKind = 'image' | 'file';

export const ISSUE_EVIDENCE_BUCKET = 'issue-evidence';

export interface EvidenceRef {
  issueId: string;
  stage: EvidenceStage;
  hypothesisId: string | null;
}

/**
 * Miniatura ou arquivo. Qualquer MIME preenchido é aceito.
 * Só image/* vira preview; o restante, inclusive vídeo, fica arquivo.
 */
export function evidenceKindForMime(mimeType: string): EvidenceKind {
  const mime = mimeType.trim().toLowerCase();
  if (!mime) throw new Error('A evidência precisa de um tipo MIME.');
  return mime.startsWith('image/') ? 'image' : 'file';
}

/** A hipótese precisa já estar gravada neste problema, com id definitivo. */
export function assertHypothesisOnIssue(
  analysis: RootCauseAnalysis | null | undefined,
  hypothesisId: string | null | undefined,
): string | null {
  const id = hypothesisId?.trim() || null;
  if (!id) return null;
  if (isLegacyHypothesisId(id)) {
    throw new Error('A evidência só pode usar o id definitivo da hipótese.');
  }
  const found = ensureIshikawa(analysis?.ishikawa).some((group) => (
    group.causes.some((cause) => cause.id === id)
  ));
  if (!found) throw new Error('A hipótese não pertence a este problema.');
  return id;
}

/**
 * O cliente de storage envia Blob como multipart para `/object/`, que espera o arquivo cru.
 * Esse desencontro responde HTTP 400 em HTML. Bytes crus preservam o content-type.
 */
export async function evidenceUploadBytes(file: Blob): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') {
    return new Uint8Array(await file.arrayBuffer());
  }
  const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('Não foi possível ler a evidência.'));
    reader.readAsArrayBuffer(file);
  });
  return new Uint8Array(buffer);
}

export function evidenceStoragePath(
  organizationId: string,
  issueId: string,
  evidenceId: string,
  fileName: string,
): string {
  const baseName = fileName.trim();
  if (!organizationId || !issueId || !evidenceId || !baseName || baseName.includes('/') || baseName.includes('\\') || baseName.includes('..')) {
    throw new Error('O caminho da evidência está incompleto.');
  }
  const safeName = baseName.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_+/g, '_');
  if (!safeName || safeName === '_' || safeName.includes('/')) {
    throw new Error('O caminho da evidência está incompleto.');
  }
  return `${organizationId}/${issueId}/${evidenceId}/${safeName}`;
}

export function evidenceMatchesContext<T extends EvidenceRef>(rows: T[], context: EvidenceRef): T[] {
  return rows.filter((row) => (
    row.issueId === context.issueId
    && row.stage === context.stage
    && row.hypothesisId === context.hypothesisId
  ));
}
